import { useState, useEffect, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { SearchInput, SkeletonText, CustomSelect } from '@/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { getCampaignLogs } from '../api';
import { TableChip } from './PremiumDesign';
import { PermissionGuard, V2Table } from '@/common/components';
import { PERMISSIONS } from '@/common/config/permissions';

const LOG_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
];

export function CampaignLogsTable({
  companyId,
  campaignId,
  onBack,
  showCampaignActions = false,
  embedded = false,
  canRetry,
  onRetry,
  isRetrying,
  canStop,
  onStop,
  isStopping,
  canPause = false,
  onPause,
  isPausing = false,
  canResume = false,
  onResume,
  isResuming = false,
  canReschedule = false,
  onReschedule,
  isRescheduling = false,
  isDetailsLoading,
}) {
  const [logSearchQuery, setLogSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentLogPage, setCurrentLogPage] = useState(1);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(logSearchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [logSearchQuery]);

  useEffect(() => {
    setCurrentLogPage(1);
  }, [debouncedSearchQuery, statusFilter]);

  const {
    data: logsData,
    isLoading: isLogsInitialLoading,
    isFetching: isLogsFetching,
  } = useQuery({
    queryKey: [
      'bulk-campaign-logs',
      companyId,
      campaignId,
      currentLogPage,
      debouncedSearchQuery,
      statusFilter,
    ],
    queryFn: () => {
      const params = { page: currentLogPage };
      if (debouncedSearchQuery) params.search = debouncedSearchQuery;
      if (statusFilter) params.status = statusFilter;
      return getCampaignLogs({ companyId, campaignId, params });
    },
    enabled: !!campaignId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isLogsFetching && logsData !== undefined,
    300,
    500,
  );
  const isLogsLoading = isLogsInitialLoading || showDelayedSkeleton;

  const logsList = logsData?.results || [];
  const logsCount = logsData?.count || 0;
  const logsPerPage = 10;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return dateStr;
    }
  };

  const renderLogStatusBadge = (status) => {
    let tone = 'neutral';
    let label = status;
    switch (String(status).toLowerCase()) {
      case 'accepted':
        tone = 'success';
        label = 'Accepted';
        break;
      case 'read':
        tone = 'success';
        label = 'Read';
        break;
      case 'delivered':
        tone = 'info';
        label = 'Delivered';
        break;
      case 'processing':
        tone = 'warning';
        label = 'Processing';
        break;
      case 'sent':
        tone = 'warning';
        label = 'Sent';
        break;
      case 'failed':
        tone = 'error';
        label = 'Failed';
        break;
      case 'rejected':
        tone = 'error';
        label = 'Rejected';
        break;
      case 'skipped':
        tone = 'warning';
        label = 'Skipped';
        break;
      case 'pending':
        tone = 'neutral';
        label = 'Pending';
        break;
      default:
        break;
    }
    return <TableChip tone={tone}>{label}</TableChip>;
  };

  const logColumns = useMemo(
    () => [
      {
        id: 'client_name',
        label: 'Recipient Name',
        defaultWidth: 200,
        minWidth: 140,
        resizable: true,
        skeleton: <SkeletonText width="w-28" />,
        cell: (log) => (
          <span
            className="truncate block font-medium text-sm text-[#0F172A]"
            title={log.client_name || ''}
          >
            {log.client_name || '-'}
          </span>
        ),
      },
      {
        id: 'client_phone',
        label: 'Phone Number',
        defaultWidth: 170,
        minWidth: 130,
        resizable: true,
        skeleton: <SkeletonText width="w-28" />,
        cell: (log) => (
          <span className="truncate block text-sm text-[#1E293B]">
            {log.client_phone || '-'}
          </span>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        defaultWidth: 130,
        minWidth: 100,
        resizable: true,
        align: 'center',
        skeleton: (
          <SkeletonText
            width="w-20"
            height="h-6"
            className="mx-auto rounded-full"
          />
        ),
        cell: (log) => {
          const statusToShow = log.delivery_status || log.status;
          return renderLogStatusBadge(statusToShow);
        },
      },
      {
        id: 'failure_reason',
        label: 'Failure Reason',
        defaultWidth: 260,
        minWidth: 160,
        resizable: true,
        skeleton: <SkeletonText width="w-36" />,
        cell: (log) => (
          <span
            className="truncate block text-sm text-[#475569]"
            title={log.failure_reason || ''}
          >
            {log.failure_reason || '-'}
          </span>
        ),
      },
      {
        id: 'sent_at',
        label: 'Sent Time',
        defaultWidth: 190,
        minWidth: 140,
        resizable: true,
        skeleton: <SkeletonText width="w-24" />,
        cell: (log) => (
          <span className="truncate block text-sm text-[#475569]">
            {formatDate(log.sent_at || log.created_at)}
          </span>
        ),
      },
    ],
    [],
  );

  const showActions =
    showCampaignActions &&
    (canReschedule || canPause || canResume || canStop || canRetry);

  const totalLabel =
    showCampaignActions || onBack
      ? `Total Records: ${logsCount}`
      : `Total: ${logsCount}`;

  return (
    <div
      className={
        embedded
          ? 'w-full min-w-0 flex flex-col gap-3'
          : 'w-full min-w-0 rounded-2xl bg-layer-5-card flex flex-col p-3 sm:p-4 gap-3'
      }
    >
      <div className="flex items-center justify-between flex-wrap gap-2 shrink-0">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="w-9 h-9 rounded-lg border border-primary text-primary hover:bg-surface-mint flex items-center justify-center transition-colors focus:outline-none shrink-0"
              title="Back to Campaigns"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}
          <h3 className="text-h6 font-bold text-text-primary">
            Recipient Delivery Logs
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {showCampaignActions &&
            (isDetailsLoading ? (
              <SkeletonText width="w-28" height="h-9" className="rounded-xl" />
            ) : (
              showActions && (
                <PermissionGuard permission={PERMISSIONS.WA_CAMPAIGNS.CREATE}>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {canReschedule && (
                      <button
                        type="button"
                        onClick={onReschedule}
                        disabled={isRescheduling}
                        className="px-4 py-2 bg-primary/10 hover:bg-primary/20 border border-primary/20 text-primary text-sm font-semibold rounded-xl transition-all focus:outline-none disabled:opacity-50"
                      >
                        {isRescheduling ? 'Rescheduling...' : 'Reschedule'}
                      </button>
                    )}
                    {canPause && (
                      <button
                        type="button"
                        onClick={onPause}
                        disabled={isPausing}
                        className="px-4 py-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-600 text-sm font-semibold rounded-xl transition-all focus:outline-none disabled:opacity-50"
                      >
                        {isPausing ? 'Pausing...' : 'Pause'}
                      </button>
                    )}
                    {canResume && (
                      <button
                        type="button"
                        onClick={onResume}
                        disabled={isResuming}
                        className="px-4 py-2 bg-primary/10 hover:bg-primary/20 border border-primary/20 text-primary text-sm font-semibold rounded-xl transition-all focus:outline-none disabled:opacity-50"
                      >
                        {isResuming ? 'Resuming...' : 'Resume'}
                      </button>
                    )}
                    {canStop && (
                      <button
                        type="button"
                        onClick={onStop}
                        disabled={isStopping}
                        className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-500 text-sm font-semibold rounded-xl transition-all focus:outline-none disabled:opacity-50"
                      >
                        {isStopping ? 'Stopping...' : 'Stop'}
                      </button>
                    )}
                    {canRetry && (
                      <button
                        type="button"
                        onClick={onRetry}
                        disabled={isRetrying}
                        className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-500 text-sm font-semibold rounded-xl transition-all focus:outline-none disabled:opacity-50"
                      >
                        {isRetrying ? 'Retrying...' : 'Retry Failed'}
                      </button>
                    )}
                  </div>
                </PermissionGuard>
              )
            ))}
          <span className="text-sm font-medium text-text-secondary flex items-center">
            {isLogsLoading ? (
              <SkeletonText
                width="w-28"
                height="h-5"
                className="inline-block"
              />
            ) : (
              totalLabel
            )}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-3 w-full sm:max-w-xl flex-wrap sm:flex-nowrap shrink-0">
        <SearchInput
          value={logSearchQuery}
          onChange={(e) => setLogSearchQuery(e.target.value)}
          placeholder="Search logs by Phone or Name"
          className="flex-1 min-w-[200px]"
        />
        <CustomSelect
          value={statusFilter}
          onChange={setStatusFilter}
          options={LOG_STATUS_OPTIONS}
          placeholder="All Statuses"
          className="w-full sm:w-[160px] h-[40px]"
          buttonClassName="h-10 border border-border-default bg-white rounded-xl w-full flex items-center justify-between px-4 text-sm font-medium text-text-primary focus:outline-none transition-all hover:border-border-active"
          textSizeClass="text-sm font-medium"
        />
      </div>

      <div className="w-full min-w-0 flex flex-col campaigns-table-compact [&_tbody_tr]:!h-[46px] [&_thead_tr]:!h-[42px] [&_td]:!py-2 [&_td]:!px-3 [&_th]:!py-2 [&_th]:!px-3">
        <V2Table
          flow
          tight
          columns={logColumns}
          data={logsList}
          rowKey="id"
          storageKey="campaign_logs_table_column_widths"
          loading={isLogsLoading || !logsData}
          loadingRowCount={5}
          empty={
            <div className="flex flex-col items-center justify-center gap-3">
              <span className="text-base font-medium">
                No logs matched this criteria.
              </span>
            </div>
          }
          pagination={{
            currentPage: currentLogPage,
            totalCount: logsCount,
            itemsPerPage: logsPerPage,
            onChange: setCurrentLogPage,
          }}
        />
      </div>
    </div>
  );
}
